import { Constraint } from '../models/constraint';
import { ConstraintEnum } from '../enum/constraint.enum';
import { cardLimits } from './card-limits';
import { Clock } from '../utils/clock';
import { ValidationMode } from '../enum/validation-mode.enum';

const required = (s: string): boolean => s === null || s === undefined || s?.length === 0;
const maxLength = (m: number, s: string): boolean => s?.length > m;
const emails = (s: string): boolean => !/^\s*[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\s*(?:,\s*[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\s*)*$/.test(s);

export const shipmentConstraints: Constraint[] = [
    {
        id: ConstraintEnum.SHIPPER_ORGANIZATION_EMPTY,
        path: 'shipment.shipper.organization',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide the name of the shipper`,
        validator: (state) => required(state.shipper.organization)
    },
    {
        id: ConstraintEnum.SHIPPER_ATTN_EMPTY,
        path: 'shipment.shipper.attn',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide the contact name of the shipper`,
        validator: (state) => required(state.shipper.attn)
    },
    {
        id: ConstraintEnum.SHIPPER_STREET1_EMPTY,
        path: 'shipment.shipper.street1',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide the first line of the street address`,
        validator: (state) => required(state.shipper.street1)
    },
    {
        id: ConstraintEnum.SHIPPER_STREET1_LENGTH,
        path: 'shipment.shipper.street1',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`The length of the street address cannot exceed ${cardLimits.street1.characters}`,
        validator: (state) => maxLength(cardLimits.street1.characters, state.shipper.street1)
    },
    {
        id: ConstraintEnum.SHIPPER_STREET2_LENGTH,
        path: 'shipment.shipper.street2',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`The length of the street address cannot exceed ${cardLimits.street2.characters}`,
        validator: (state) => maxLength(cardLimits.street2.characters, state.shipper.street2)
    },
    {
        id: ConstraintEnum.SHIPPER_TELEPHONE_EMPTY,
        path: 'shipment.shipper.telephone',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide a shipper telephone number`,
        validator: (state) => required(state.shipper.telephone)
    },
    {
        id: ConstraintEnum.SHIPPER_EMAIL_EMPTY,
        path: 'shipment.shipper.email',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide a shipper email address`,
        validator: (state) => required(state.shipper.email)
    },
    {
        id: ConstraintEnum.SHIPPER_EMAIL_INVALID,
        path: 'shipment.shipper.email',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`Invalid shipper email address(es)`,
        validator: (state) => emails(state.shipper.email)
    },
    {
        id: ConstraintEnum.SHIPPER_COUNTRY_EMPTY,
        path: 'shipment.shipper.country',
        mode: ValidationMode.QUOTE,
        message: () => $localize`You must select a shipper country`,
        validator: (state) => required(state.shipper.country)
    },
    {
        id: ConstraintEnum.DESTINATION_ORGANIZATION_EMPTY,
        path: 'shipment.destination.organization',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide the name of the destination`,
        validator: (state) => required(state.destination.organization)
    },
    {
        id: ConstraintEnum.DESTINATION_ATTN_EMPTY,
        path: 'shipment.destination.attn',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide the contact name of the destination`,
        validator: (state) => required(state.destination.attn)
    },
    {
        id: ConstraintEnum.DESTINATION_STREET1_EMPTY,
        path: 'shipment.destination.street1',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide the first line of the street address`,
        validator: (state) => required(state.destination.street1)
    },
    {
        id: ConstraintEnum.DESTINATION_STREET1_LENGTH,
        path: 'shipment.destination.street1',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`The length of the street address cannot exceed ${cardLimits.street1.characters}`,
        validator: (state) => maxLength(cardLimits.street1.characters, state.destination.street1)
    },
    {
        id: ConstraintEnum.DESTINATION_STREET2_LENGTH,
        path: 'shipment.destination.street2',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`The length of the street address cannot exceed ${cardLimits.street2.characters}`,
        validator: (state) => maxLength(cardLimits.street2.characters, state.destination.street2)
    },
    {
        id: ConstraintEnum.DESTINATION_TELEPHONE_EMPTY,
        path: 'shipment.destination.telephone',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide a destination telephone number`,
        validator: (state) => required(state.destination.telephone)
    },
    {
        id: ConstraintEnum.DESTINATION_EMAIL_EMPTY,
        path: 'shipment.destination.email',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must provide a destination email address`,
        validator: (state) => required(state.destination.email)
    },
    {
        id: ConstraintEnum.DESTINATION_EMAIL_INVALID,
        path: 'shipment.destination.email',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`Invalid destination email address(es)`,
        validator: (state) => emails(state.destination.email)
    },
    {
        id: ConstraintEnum.DESTINATION_COUNTRY_EMPTY,
        path: 'shipment.destination.country',
        mode: ValidationMode.QUOTE,
        message: () => $localize`You must select a destination country`,
        validator: (state) => required(state.destination.country)
    },
    {
        id: ConstraintEnum.SHIPMENT_DATE_PAST,
        path: 'shipment.shipmentDate',
        mode: ValidationMode.QUOTE,
        message: () => $localize`Shipment Date cannot be in the past`,
        validator: (state) => state.shipmentDate < Clock.nowIso().substring(0, 10)
    },
    {
        id: ConstraintEnum.NO_PACKAGES,
        path: 'shipment.packages',
        mode: ValidationMode.QUOTE,
        message: () => $localize`You must add at least one package`,
        validator: (state) => state.packages.length === 0
    },
    {
        id: ConstraintEnum.NO_COMMODITIES,
        path: 'shipment.commodities',
        mode: ValidationMode.SUBMIT,
        message: () => $localize`You must specify at least one commodity for international non-documents shipments`,
        validator: (state) =>
            state.shipper.country !== state.destination.country
            && state.documentsOnly === false
            && state.packages.filter(p => p.commodities.length > 0).length === 0
    }
];

